import { useAuthContext } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { List, Logout, Manage, MyAccount, MySubscribedWebinar, Plus, Support, SwitchAccount } from '@core/ui';
import { Link } from '@online-webinar/router';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDismissableMenu } from '../../hooks/useDismissableMenu';
import { useScheduleSectionProps } from '../../hooks/useScheduleSectionProps';

interface Props {
    logoUrl: string;
    /** The words shown in the logo (site settings `logoText`), the start of the logo link's name */
    logoText: string;
}

export const TopHeader = ({ logoUrl, logoText }: Props) => {
    const { t } = useTranslation();
    const scheduleSectionProps = useScheduleSectionProps();
    const { isAuthenticated, isMainAccount, canBroadcast, hasRights, managedAccounts, updateAuthState } = useAuthContext();

    const [isMobileMenu, setIsMobileMenu] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuBreakpoint = useMemo(() => {
        let fontSize = 16;
        if (globalThis.document && globalThis.getComputedStyle) {
            fontSize = parseFloat(globalThis.getComputedStyle(globalThis.document.documentElement).fontSize);
        }
        return 92 * fontSize;
    }, []);

    useLayoutEffect(() => {
        if (!globalThis.window) {
            return;
        }

        const handleResize = debounce(() => {
            // 92rem is the breakpoint where the mobile menu is toggled
            if (globalThis.window.innerWidth >= menuBreakpoint) {
                setIsMobileMenu(false);
            } else {
                setIsMobileMenu(true);
            }
        }, 50);

        globalThis.window.addEventListener('resize', handleResize);
        handleResize();

        //document.addEventListener("click", closeMenu, true)
        // Clean up the event listener on component unmount
        return () => {
            globalThis.window.removeEventListener('resize', handleResize);
            //document.removeEventListener("click", closeMenu, true);
        };
    }, []);

    const handleClick = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const mobileMenuToggleRef = useRef<HTMLButtonElement>(null);
    const dismissMenu = useDismissableMenu(isMobileMenu && isMenuOpen, () => setIsMenuOpen(false), mobileMenuToggleRef);

    // The desktop account menu is a disclosure too: close it when focus leaves it, or on Escape
    const accountButtonRef = useRef<HTMLButtonElement>(null);
    const accountMenuId = useId();
    const dismissAccountMenu = useDismissableMenu(!isMobileMenu && isMenuOpen, () => setIsMenuOpen(false), accountButtonRef);

    const { mutate: logout } = useMutation(
        OptionsApi.login.logout({
            onSuccess: () => {
                updateAuthState({ isAuthenticated: false, token: null, mainAccount: null, activeAccount: null, managedAccounts: [], rights: [] });
            }
        })
    );

    return (
        <header className="subs-header" data-testid="whitelabel-header">
            <div className="relative flex items-center px-4 md:px-8 lg:px-12">
                <a className="subs-skiplink" href="#main-content">
                    {t('Generic.Skiplink')}
                </a>
                <a className="company-logo" href="/">
                    <img src={logoUrl} alt={t('Generic.Link.Logo', { site: logoText, page: t('Schedule.PageHeading') })} />
                </a>
                {isAuthenticated && (
                    <>
                        {isMobileMenu ? (
                            // The mobile menu slides over the page: close it when focus leaves it, or on Escape.
                            // display: contents keeps the overlay, toggle and menu laid out as before.
                            <div className="contents" {...dismissMenu}>
                                <div
                                    onClick={handleClick}
                                    className={`subs-nav-overlay ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'} block absolute top-full left-0 w-full h-screen backdrop-blur-xs transition-opacity ease-out-quint duration-300 motion-reduce:duration-0`}
                                ></div>
                                <button
                                    ref={mobileMenuToggleRef}
                                    className="relative size-12 flex-none bg-transparent rounded-xl ml-auto z-10"
                                    onClick={handleClick}
                                    aria-expanded={isMenuOpen}
                                    aria-label={`${isMenuOpen ? t('MainMenu.Toggle.Close') : t('MainMenu.Toggle.Open')}`}
                                >
                                    <span
                                        className={`absolute left-2 w-8 h-0.5 bg-black block rounded-xs motion-reduce:[animation-duration:0s] translate-y-[calc(-50%-.375rem)] ${isMenuOpen ? 'animate-menuToggleToCloseTop' : 'animate-menuToggleToOpenTop'}`}
                                    ></span>
                                    <span
                                        className={`absolute left-2 w-8 h-0.5 bg-black block rounded-xs motion-reduce:[animation-duration:0s] ${isMenuOpen ? 'animate-menuToggleToCloseCenter' : 'animate-menuToggleToOpenCenter'}`}
                                    ></span>
                                    <span
                                        className={`absolute left-2 w-8 h-0.5 bg-black block rounded-xs motion-reduce:[animation-duration:0s] translate-y-[calc(50%+.375rem)] ${isMenuOpen ? 'animate-menuToggleToCloseBottom' : 'animate-menuToggleToOpenBottom'}`}
                                    ></span>
                                </button>
                                <div className={`subs-nav-wrapper ${isMenuOpen ? '--open' : '--closed'}`}>
                                    <nav className="subs-main-menu flex-col" aria-label={t('MainMenu.Label')} onClick={(e) => { if ((e.target as HTMLElement).closest('a, button')) setIsMenuOpen(false); }}>
                                        <ul>
                                            {isMainAccount && (
                                                <li>
                                                    <Link to="/upcoming" inactiveProps={scheduleSectionProps}>
                                                        <MySubscribedWebinar classNames="size-6 mr-2 stroke-current" />
                                                        {t('MainMenu.Schedule')}
                                                    </Link>
                                                </li>
                                            )}
                                            {canBroadcast && (
                                                <>
                                                    <li>
                                                        <Link to="/create">
                                                            <Plus classNames="size-6 mr-2 stroke-current" />
                                                            {t('MainMenu.CreateNew')}
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link to="/organized">
                                                            <List classNames="size-6 mr-2 stroke-current" />
                                                            {t('MainMenu.Overview')}
                                                        </Link>
                                                    </li>
                                                    {hasRights && (
                                                        <li>
                                                            <Link to="/manage">
                                                                <Manage classNames="size-6 mr-2 stroke-current" />
                                                                {t('MainMenu.ManageEnv')}
                                                            </Link>
                                                        </li>
                                                    )}
                                                </>
                                            )}
                                            <li>
                                                <a href="https://www.onlinewebinar.com/nl/webinar-support/">
                                                    <Support classNames="size-6 mr-2 stroke-current" />
                                                    {t('MainMenu.Support')}
                                                </a>
                                            </li>
                                            {isMainAccount && (
                                                <li className="menu-divider">
                                                    <Link to="/account/$tab" params={{ tab: 'profile' }}>
                                                        <MyAccount classNames="size-6 mr-2 stroke-current" />
                                                        {t('Account.Menu.Profile')}
                                                    </Link>
                                                </li>
                                            )}
                                            {!isMainAccount && (
                                                <li className="menu-divider">
                                                    <span className="text-sm">{t('Account.Menu.NotOnMainAccount')}</span>
                                                </li>
                                            )}
                                            {managedAccounts?.length > 0 && (
                                                <li>
                                                    <Link to="/switch-account">
                                                        <SwitchAccount classNames="size-6 mr-2 stroke-current" />
                                                        {t('Account.Menu.SwitchAccount')}
                                                    </Link>
                                                </li>
                                            )}
                                            <li>
                                                <button onClick={() => logout()}>
                                                    <Logout classNames="size-6 mr-2 stroke-current" />
                                                    {t('Account.Menu.Logout')}
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div
                                    onClick={handleClick}
                                    className={`${isMenuOpen ? 'translate-y-0' : 'translate-y-full'} block absolute top-full left-0 w-screen h-screen`}
                                ></div>
                                <nav className="subs-main-menu flex-row" aria-label={t('MainMenu.Label')}>
                                    <ul>
                                        {isMainAccount && (
                                            <li>
                                                <Link to="/upcoming" inactiveProps={scheduleSectionProps}>
                                                    <MySubscribedWebinar classNames="size-6 mr-2 stroke-current" />
                                                    {t('MainMenu.Schedule')}
                                                </Link>
                                            </li>
                                        )}
                                        {canBroadcast && (
                                            <>
                                                <li>
                                                    <Link to="/create">
                                                        <Plus classNames="size-6 mr-2 stroke-current" />
                                                        {t('MainMenu.CreateNew')}
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link to="/organized">
                                                        <List classNames="size-6 mr-2 stroke-current" />
                                                        {t('MainMenu.Overview')}
                                                    </Link>
                                                </li>
                                                {hasRights && (
                                                    <li>
                                                        <Link to="/manage">
                                                            <Manage classNames="size-6 mr-2 stroke-current" />
                                                            {t('MainMenu.ManageEnv')}
                                                        </Link>
                                                    </li>
                                                )}
                                            </>
                                        )}
                                        <li>
                                            <a href="https://www.onlinewebinar.com/nl/webinar-support/">
                                                <Support classNames="size-6 mr-2 stroke-current" />
                                                {t('MainMenu.Support')}
                                            </a>
                                        </li>
                                    </ul>
                                </nav>
                                <div className="relative md:ml-auto" {...dismissAccountMenu}>
                                    <button ref={accountButtonRef} className="subs-acc-btn" aria-expanded={isMenuOpen} aria-controls={accountMenuId} onClick={handleClick}>
                                        <MyAccount classNames="flex-none size-6 mr-2 stroke-current" />
                                        {t('Account.Button')}
                                    </button>
                                    <ul
                                        id={accountMenuId}
                                        aria-label={t('Account.Button')}
                                        className={`subs-acc-menu ${isMenuOpen ? '' : 'hidden'}`}
                                        // Choosing an item closes the menu; in-app navigation keeps the header mounted
                                        onClick={(e) => {
                                            if ((e.target as HTMLElement).closest('a, button')) setIsMenuOpen(false);
                                        }}
                                    >
                                        {managedAccounts?.length > 0 && (
                                            <li>
                                                <Link to="/switch-account">
                                                    {t('Account.Menu.SwitchAccount')}
                                                </Link>
                                            </li>
                                        )}
                                        {isMainAccount && (
                                            <>
                                                <li>
                                                    <Link to="/account/$tab" params={{ tab: 'profile' }}>
                                                        {t('Account.Menu.Profile')}
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link to="/account/$tab" params={{ tab: 'password' }}>
                                                        {t('Account.Menu.Password')}
                                                    </Link>
                                                </li>
                                                {hasRights && (
                                                    <li>
                                                        <Link to="/account/$tab" params={{ tab: 'manage' }}>
                                                            {t('Account.Menu.ManageAccount')}
                                                        </Link>
                                                    </li>
                                                )}
                                            </>
                                        )}
                                        {!isMainAccount && (
                                            <li>
                                                <span className="text-sm">{t('Account.Menu.NotOnMainAccount')}</span>
                                            </li>
                                        )}
                                        <li>
                                            <button onClick={() => logout()}>
                                                {t('Account.Menu.Logout')}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </header>
    );
};
